import { NodeSelection } from '@tiptap/pm/state';
import {
  ArrowLeftSmall,
  ArrowRightSmall,
  DeleteSmall,
  PaddingLeftSmall,
  PaddingRightSmall,
} from '@wix/wix-ui-icons-common';

import { TIPTAP_LAYOUT_CELL_TYPE } from './../../ricos-content';
import type { EditorPlugin, RicosServices } from './../../ricos-types';
import { RicosPluginToolbarNames } from './../../ricos-types';
import { resolversById } from './../../toolbars-v3';
import { RESOLVERS_IDS } from './../../toolbars-v3/resolvers/resolvers-ids';

import { cellConverter } from './cell-converter';
import { cellExtension } from './cell-extension';
import {
  LAYOUT_CELL_TOOLBAR_ADD_LEFT_BUTTON,
  LAYOUT_CELL_TOOLBAR_ADD_RIGHT_BUTTON,
  LAYOUT_CELL_TOOLBAR_DELETE_BUTTON,
  LAYOUT_CELL_TOOLBAR_MOVE_LEFT_BUTTON,
  LAYOUT_CELL_TOOLBAR_MOVE_RIGHT_BUTTON,
} from './data-hooks';
import { getToolbarReferenceElement, type LayoutEditorConfig } from './shared';

export const pluginCell = (
  config: LayoutEditorConfig = { showInQuickActions: false, showInToolbars: false }
): EditorPlugin => ({
  type: TIPTAP_LAYOUT_CELL_TYPE,
  config,
  pmMarkConverters: [],
  pmNodeConverters: [cellConverter],
  tiptapExtensions: [cellExtension],
  toolbar: {
    names: [RicosPluginToolbarNames.LAYOUT_CELL],
    resolveReferenceElement: tiptapEditor =>
      getToolbarReferenceElement(tiptapEditor, '[data-layout-cell-handle]'),
    getButtons: (_, services: RicosServices) =>
      services.context.isMobile
        ? []
        : config.showInQuickActions || config.showInToolbars
        ? [
            {
              id: 'addCellLeft',
              dataHook: LAYOUT_CELL_TOOLBAR_ADD_LEFT_BUTTON,
              tooltip: 'Layouts_CellAddLeft_Tooltip',
              icon: PaddingLeftSmall,
              attributes: {
                alwaysVisible: resolversById[RESOLVERS_IDS.ALWAYS_VISIBLE],
                disabled: {
                  id: 'addCellLeft',
                  resolve: (_, __, editor) => {
                    const selection = editor?.state.selection;
                    if (editor && selection instanceof NodeSelection) {
                      const id = selection.node?.attrs.id;
                      return !editor.can().addCellLeft(id);
                    }
                    return true;
                  },
                },
              },
              command: ({ tiptapEditor, attributes }) => {
                tiptapEditor.commands.addCellLeft(attributes.selectedNode.attrs.id);
              },
            },
            {
              id: 'addCellRight',
              dataHook: LAYOUT_CELL_TOOLBAR_ADD_RIGHT_BUTTON,
              tooltip: 'Layouts_CellAddRight_Tooltip',
              icon: PaddingRightSmall,
              attributes: {
                alwaysVisible: resolversById[RESOLVERS_IDS.ALWAYS_VISIBLE],
                disabled: {
                  id: 'addCellRight',
                  resolve: (_, __, editor) => {
                    const selection = editor?.state.selection;
                    if (editor && selection instanceof NodeSelection) {
                      const id = selection.node?.attrs.id;
                      return !editor.can().addCellRight(id);
                    }
                    return true;
                  },
                },
              },
              command: ({ tiptapEditor, attributes }) => {
                tiptapEditor.commands.addCellRight(attributes.selectedNode.attrs.id);
              },
            },
            {
              id: 'deleteCell',
              dataHook: LAYOUT_CELL_TOOLBAR_DELETE_BUTTON,
              tooltip: 'Layouts_CellDelete_Tooltip',
              icon: DeleteSmall,
              command: ({ tiptapEditor, attributes }) => {
                tiptapEditor.commands.deleteCell(attributes.selectedNode.attrs.id);
              },
            },
            {
              id: 'moveCellLeft',
              dataHook: LAYOUT_CELL_TOOLBAR_MOVE_LEFT_BUTTON,
              tooltip: 'Layouts_CellMoveLeft_Tooltip',
              icon: ArrowLeftSmall,
              attributes: {
                alwaysVisible: resolversById[RESOLVERS_IDS.ALWAYS_VISIBLE],
                disabled: {
                  id: 'moveCellLeft',
                  resolve: (_, __, editor) => {
                    const selection = editor?.state.selection;
                    if (editor && selection instanceof NodeSelection) {
                      const id = selection.node?.attrs.id;
                      return !editor.can().moveCellLeft(id);
                    }
                    return true;
                  },
                },
              },
              command: ({ tiptapEditor, attributes }) => {
                tiptapEditor.commands.moveCellLeft(attributes.selectedNode.attrs.id);
              },
            },
            {
              id: 'moveCellRight',
              dataHook: LAYOUT_CELL_TOOLBAR_MOVE_RIGHT_BUTTON,
              tooltip: 'Layouts_CellMoveRight_Tooltip',
              icon: ArrowRightSmall,
              attributes: {
                alwaysVisible: resolversById[RESOLVERS_IDS.ALWAYS_VISIBLE],
                disabled: {
                  id: 'moveCellRight',
                  resolve: (_, __, editor) => {
                    const selection = editor?.state.selection;
                    if (editor && selection instanceof NodeSelection) {
                      const id = selection.node?.attrs.id;
                      return !editor.can().moveCellRight(id);
                    }
                    return true;
                  },
                },
              },
              command: ({ tiptapEditor, attributes }) => {
                tiptapEditor.commands.moveCellRight(attributes.selectedNode.attrs.id);
              },
            },
          ]
        : [],
  },
});
